/**
 * String ids at the boundary ↔ numeric serials inside (spec §3.2). Inside the core everything is an index;
 * ids are formatted for views, commands, saves and the manifest, and parsed back from commands and saves.
 * Station ids are stable strings: `sys-07-st2` for stations the core creates, M3's ids for adopted sites,
 * and M2's `hub`/`refinery`/`scrapyard` in the fallback home system. Look stations up through
 * `state.world.stationById`, not by parsing.
 */
import { systemId } from '../galaxy/placement'

export function agentId(seq: number): string {
  return `ag-${seq}`
}

/**
 * A station the core creates in system `sysIndex`: `<systemId>-st<n>` (e.g. `sys-07-st2`). Callers pick the
 * lowest `n` not already in `world.stationById` (adopted M3 site ids may follow the same pattern).
 */
export function stationId(sysIndex: number, n: number): string {
  return `${systemId(sysIndex)}-st${n}`
}

export function offscreenWreckId(seq: number): string {
  return `ow-${seq}`
}

export function siteId(seq: number): string {
  return `site-${seq}`
}

export function groupId(seq: number): string {
  return `grp-${seq}`
}

export function orderId(seq: number): string {
  return `ord-${seq}`
}

const SEQ = /^(?:0|[1-9]\d{0,9})$/

/** The serial after `prefix-`, or −1 when `id` is not `<prefix>-<non-negative integer>`. */
function parseSeq(prefix: string, id: string): number {
  if (!id.startsWith(`${prefix}-`)) return -1
  const digits = id.slice(prefix.length + 1)
  if (!SEQ.test(digits)) return -1
  const n = Number(digits)
  return n <= 0xffffffff ? n : -1
}

export function parseAgentId(id: string): number {
  return parseSeq('ag', id)
}

export function parseOffscreenWreckId(id: string): number {
  return parseSeq('ow', id)
}

export function parseSiteId(id: string): number {
  return parseSeq('site', id)
}

export function parseGroupId(id: string): number {
  return parseSeq('grp', id)
}

export function parseOrderId(id: string): number {
  return parseSeq('ord', id)
}

const STATION = /^sys-(\d{2,})-st(0|[1-9]\d*)$/

/** Parses a core-created station id (`sys-07-st2` → { sysIndex: 7, n: 2 }); null for any other id. */
export function parseStationId(id: string): { sysIndex: number; n: number } | null {
  const m = STATION.exec(id)
  if (m === null) return null
  const sysIndex = Number(m[1])
  if (systemId(sysIndex) !== `sys-${m[1]}`) return null
  return { sysIndex, n: Number(m[2]) }
}
